import React, { useState, useCallback, useEffect } from 'react';
import { Button, Input, Select, Typography, showToast } from '@attentive-platform/stem-ui';
import { CompanyInfoSection, ContractTermsSection, BillingInfoSection } from '../SharedComponent';
import MultiTradeSection from './MultiTradeSection';
import { fetchApiData } from '../../Utils/ApiCalls';
import {
  addOneYearToDate,
  normalizeBillingFrequency,
  toDateOnly,
  resolveTotalUsersFromEnterpriseInfo
} from '../../Utils/HelperFunctions';
import {
  BILLING_FREQUENCY_ENUM,
  COUNTRY_CODE,
  commercialModelToDfyModel,
  INITIAL_FORM_DATA
} from '../../Constants/Constants';
import {
  DFY_MODEL_SELECT_ITEMS,
  getEmptyBeamContractPricing,
  unwrapEnterpriseInfoPayload,
  mergeSharedContractTermsForForm,
  pickPlatformAccessFromEnterpriseInfo,
  resolveCompanyInfoSliceFromEnterpriseInfo,
  extractBillingDetailsFromEnterpriseInfo
} from '../../Utils/beamEnterpriseShared';
import { COMPANY_SCOPES, GET_CATEGORIES } from '../../urls';
import DIYEntitlementSection from './DIYEntitlementSection';
import InfoBanner from './InfoBanner';

const commaSeparatedList = v => (Array.isArray(v) ? v.join(', ') : (v ?? ''));

/** Maps enterprise_info GET payload into parent `formData` for Beam conversion. */
const mergeEnterpriseInfoIntoFormData = (prev, { ci, sct, data, ea }) => ({
  ...prev,
  company_vertical: ci.business_unit ?? '',
  account_segment: ci.account_segment ?? '',
  key_account_manager: commaSeparatedList(ci.key_account_managers),
  onboarding_specialist: commaSeparatedList(ci.onboarding_specialist),
  total_users: (() => {
    const r = resolveTotalUsersFromEnterpriseInfo(data, sct);
    return r !== '' ? r : String(prev.total_users ?? '');
  })(),
  apply_role_level_restriction: ea.apply_role_level_restriction ?? false,
  ignore_concurrency_limit: ea.ignore_concurrency_limit ?? false,
  ignore_draft_limit: ea.ignore_draft_limit ?? false,
  ignore_marketing_mailer: ea.ignore_marketing_mailer ?? false,
  is_pilot: ea.is_pilot ?? false,
  concurrency_value: ea.concurrency_value ?? prev.concurrency_value ?? INITIAL_FORM_DATA.concurrency_value,
  subdomain: ea.subdomain ?? prev.subdomain ?? '',
  callback_url: ea.callback_url ?? prev.callback_url ?? ''
});

const clearedEnterpriseLookupFormSlice = prev => ({
  ...prev,
  company_vertical: '',
  account_segment: '',
  key_account_manager: '',
  onboarding_specialist: '',
  total_users: '',
  apply_role_level_restriction: INITIAL_FORM_DATA.apply_role_level_restriction,
  ignore_concurrency_limit: INITIAL_FORM_DATA.ignore_concurrency_limit,
  ignore_draft_limit: INITIAL_FORM_DATA.ignore_draft_limit,
  ignore_marketing_mailer: INITIAL_FORM_DATA.ignore_marketing_mailer,
  is_pilot: false,
  concurrency_value: INITIAL_FORM_DATA.concurrency_value,
  subdomain: INITIAL_FORM_DATA.subdomain,
  callback_url: INITIAL_FORM_DATA.callback_url
});

const SectionTag = ({ type, children }) => {
  const colors = {
    existing: 'bg-gray-100 text-gray-500 border-gray-200',
    new: 'bg-green-50 text-green-700 border-green-200',
    moved: 'bg-blue-50 text-blue-700 border-blue-200'
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded border font-normal ml-2 ${colors[type] || colors.existing}`}>
      {children}
    </span>
  );
};

const FieldWrapper = ({ label, required, help, children }) => (
  <div className='flex flex-col gap-1 mb-3'>
    {label && (
      <Typography variant='body4' color='var(--color-neutral-80)'>
        {label}
        {required && <span className='text-red-500 ml-0.5'>*</span>}
      </Typography>
    )}
    {children}
    {help && (
      <Typography variant='body4' color='var(--color-neutral-50)' sx={{ fontSize: '11.5px', lineHeight: 1.4 }}>
        {help}
      </Typography>
    )}
  </div>
);

const SectionCard = ({ title, tag, tagType = 'existing', children, accent }) => {
  const accentBorder = {
    blue: 'border-blue-100 bg-blue-50/20',
    purple: 'border-purple-100 bg-purple-50/10',
    green: 'border-green-100 bg-green-50/20',
    gray: 'border-gray-100 bg-gray-50',
    default: 'border-gray-100'
  };
  return (
    <div className={`border rounded-lg p-4 mb-4 ${accentBorder[accent] || accentBorder.default}`}>
      <div className='flex items-center gap-1 mb-3 pb-2 border-b border-gray-100'>
        <Typography variant='subtitle1-medium' color='var(--color-neutral-80)'>
          {title}
        </Typography>
        {tag && <SectionTag type={tagType}>{tag}</SectionTag>}
      </div>
      {children}
    </div>
  );
};

const BeamEntScreen = ({
  email = '',
  contractInfo,
  setContractInfo,
  formData,
  setFormData,
  dfyModel,
  setDfyModel,
  dfyConcurrency,
  setDfyConcurrency,
  dfyJobCap,
  setDfyJobCap,
  tierSchedule,
  setTierSchedule,
  showTierEditor,
  setShowTierEditor,
  switchConfirmed,
  setSwitchConfirmed,
  diyForm,
  setDiyForm,
  categories,
  setCategories,
  selectedCategories,
  setSelectedCategories,
  contractPricing,
  setContractPricing,
  sopConfigured,
  setSopConfigured,
  onEnterpriseInfoLoadedChange,
  setTradeTableData,
  updateEnterprise = false,
  requestEnterpriseInfo
}) => {
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isFsSynced, setIsFsSynced] = useState(false);
  const [topSectionReadOnly, setTopSectionReadOnly] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  /** When null, API did not set `summary_tiles.dfy_model` — show editable DFY model (convert-style). */
  const [summaryTilesDfyModel, setSummaryTilesDfyModel] = useState(null);

  const companyFormSlice = {
    company_vertical: formData?.company_vertical ?? '',
    account_segment: formData?.account_segment ?? '',
    key_account_manager: formData?.key_account_manager ?? '',
    onboarding_specialist: formData?.onboarding_specialist ?? '',
    total_users: formData?.total_users ?? ''
  };

  const diyRemaining = Math.max(0, (Number(diyForm.diyJobCap) || 0) - (diyForm.diyUsed || 0));

  const handleDfyModelChange = val => {
    if (updateEnterprise && dfyModel === 'usage_based' && val === 'license_based') {
      setShowSwitchConfirm(true);
      return;
    }
    if (val === 'usage_based') {
      setSwitchConfirmed(false);
    }
    setDfyModel(val);
  };

  const handleSwitchConfirm = () => {
    setSwitchConfirmed(true);
    setDfyModel('license_based');
    setShowSwitchConfirm(false);
  };

  const fetchEnterpriseInfo = useCallback(async () => {
    setLoading(true);
    try {
      const result = await requestEnterpriseInfo(email);
      if (!result?.ok) return;

      const data = unwrapEnterpriseInfoPayload(result.data);
      const cs = data?.commercial_setup ?? data?.commercialSetup ?? {};
      const ubp = data?.usage_based_pricing ?? data?.usageBasedPricing ?? {};
      const ea = data?.enterprise_attributes ?? data?.enterpriseAttributes ?? {};
      const sctMerged = mergeSharedContractTermsForForm(data);
      const platformAccessPick = pickPlatformAccessFromEnterpriseInfo(data);

      const fsSynced = data?.variant?.is_fs_synced ?? false;
      setIsFsSynced(fsSynced);

      if (updateEnterprise) {
        const ci = resolveCompanyInfoSliceFromEnterpriseInfo(data);
        setTopSectionReadOnly(data?.variant?.top_section_read_only ?? fsSynced);
        setFormData(prev => mergeEnterpriseInfoIntoFormData(prev, { ci, sct: sctMerged, data, ea }));

        const bd = extractBillingDetailsFromEnterpriseInfo(data);
        setContractInfo(prev => ({
          ...prev,
          company_name: bd.name ?? '',
          billing_email: bd.email ?? '',
          address: bd.address ?? '',
          city: bd.city ?? '',
          zip: bd.zip ?? '',
          country: bd.country ?? '',
          start_date: toDateOnly(sctMerged.contract_start_date ?? ''),
          end_date: toDateOnly(sctMerged.contract_end_date ?? ''),
          platformAccessStartDate: toDateOnly(platformAccessPick.start_date ?? ''),
          platformAccessDate: toDateOnly(
            sctMerged.platform_access_end_date ?? sctMerged.contract_end_date ?? platformAccessPick.end_date ?? ''
          ),
          billing_frequency: normalizeBillingFrequency(ubp.billing_frequency ?? prev.billing_frequency)
        }));
      } else {
        setTopSectionReadOnly(fsSynced);
        const meta = data?.fs_synced_metadata || {};
        const plan = data?.current_plan || {};
        let sct = { ...(data?.enterprise_contract || {}) };
        if (fsSynced && !sct.start_date && plan.start_date) {
          sct = {
            ...sct,
            contract_start_date: plan.start_date,
            contract_end_date: sct.contract_end_date ?? plan.end_date ?? ''
          };
        }

        const ci = fsSynced
          ? {
              business_unit: meta.business_unit ?? '',
              account_segment: meta.account_segment ?? '',
              key_account_managers: meta.key_account_manager,
              onboarding_specialist: meta.onboarding_specialist
            }
          : {
              business_unit: '',
              account_segment: '',
              key_account_managers: '',
              onboarding_specialist: ''
            };

        const seatsOrUsers = fsSynced && data?.total_users != null ? data?.total_users : '';

        setFormData(prev => {
          const merged = mergeEnterpriseInfoIntoFormData(prev, {
            ci,
            sct: { ...sct, seats: seatsOrUsers },
            data: { ...data, total_users: seatsOrUsers !== '' ? seatsOrUsers : data?.total_users },
            ea
          });
          if (!fsSynced) {
            return {
              ...merged,
              total_users: '',
              company_vertical: '',
              account_segment: '',
              key_account_manager: '',
              onboarding_specialist: ''
            };
          }
          return merged;
        });


        const bd = fsSynced ? extractBillingDetailsFromEnterpriseInfo(data) : {};
        setContractInfo(prev => ({
          ...prev,
          company_name: bd.name ?? '',
          billing_email: bd.email ?? '',
          address: bd.address ?? '',
          city: bd.city ?? '',
          zip: bd.zip ?? '',
          country: bd.country ?? '',
          start_date: fsSynced ? toDateOnly(sct.contract_start_date ?? '') : '',
          end_date: fsSynced ? toDateOnly(sct.contract_end_date ?? '') : '',
          platformAccessDate: fsSynced ? toDateOnly(data.platform_access?.end_date ?? sct.contract_end_date ?? '') : '',
          billing_frequency: normalizeBillingFrequency(ubp.billing_frequency ?? prev.billing_frequency)
        }));
      }

      setSummaryTilesDfyModel(data?.summary_tiles?.dfy_model ?? null);
      setDfyModel(commercialModelToDfyModel(cs.commercial_model));
      const dfyConcurrencyValue = cs.dfy_concurrency_limit ?? ea.concurrency_value ?? '';
      setDfyConcurrency(dfyConcurrencyValue);
      setFormData(prev => ({
        ...prev,
        concurrency_value: dfyConcurrencyValue === '' ? prev.concurrency_value : Number(dfyConcurrencyValue)
      }));
      setDfyJobCap(cs.dfy_job_cap ?? '');
      const rawTiers = cs.dfy_tier_schedule ?? [];
      setTierSchedule(
        rawTiers.map(row => ({
          max_sheets: row?.max_sheets ?? row?.threshold ?? '',
          units: row?.units ?? ''
        }))
      );
      setDiyForm({
        diyEnabled: cs.diy_enabled ?? false,
        diyJobCap: cs.diy_job_cap ?? '',
        diyUsed: data?.license_counters?.diy_jobs_used ?? 0,
        allowedTrades: (cs.allowed_diy_ai_trades ?? []).map(t =>
          t && typeof t === 'object' ? { id: t.id ?? t.value, name: t.name ?? t.display_name ?? '' } : { id: t, name: '' }
        )
      });

      const twp = ubp.trade_wise_pricing || {};
      setSelectedCategories(Object.keys(twp).filter(k => k !== 'others'));
      setContractPricing({
        bpt_contract_amount: ubp.bpt_contract_amount ?? '',
        workable_sheet_cost: ubp.workable_sheet_cost ?? '',
        addendum_sheet_cost: ubp.addendum_sheet_cost ?? '',
        manual_worksheet_cost: ubp.manual_worksheet_cost ?? '',
        billing_frequency: normalizeBillingFrequency(ubp.billing_frequency),
        trade_wise_pricing: Object.keys(twp).length ? twp : { others: { fresh: 0, addendum: 0 } }
      });



      try {
        const scopesRes = await fetchApiData({
          endpoint: COMPANY_SCOPES,
          apiRequest: 'GET',
          params: { user_email: email }
        });
        const scopeAdded =
          Array.isArray(scopesRes?.data?.data) &&
          scopesRes.data.data.some(
            t =>
              (t.assigned_included_scopes?.length ?? 0) > 0 ||
              (t.assigned_additional_scopes?.length ?? 0) > 0
          );
        setSopConfigured(scopeAdded);
      } catch {
        setSopConfigured(false);
      }

      setDataLoaded(true);
      onEnterpriseInfoLoadedChange?.(true);
    } catch {
      showToast('Failed to fetch enterprise details', 'error');
    } finally {
      setLoading(false);
    }
  }, [email, requestEnterpriseInfo, updateEnterprise, setDfyModel, setDfyConcurrency, setDfyJobCap, setTierSchedule, setDiyForm, setSelectedCategories, setContractPricing, onEnterpriseInfoLoadedChange, setFormData, setSopConfigured, setContractInfo]);

  const handleClear = () => {
    setDataLoaded(false);
    onEnterpriseInfoLoadedChange?.(false);
    setIsFsSynced(false);
    setTopSectionReadOnly(false);
    setSopConfigured(false);
    setFormData(prev => clearedEnterpriseLookupFormSlice(prev));
    setTradeTableData?.([]);
    setContractInfo(prev => ({
      ...prev,
      company_name: '',
      billing_email: '',
      address: '',
      city: '',
      zip: '',
      country: COUNTRY_CODE.USA,
      start_date: '',
      end_date: '',
      platformAccessStartDate: '',
      platformAccessDate: '',
      billing_frequency: prev.billing_frequency || BILLING_FREQUENCY_ENUM.QUARTERLY
    }));
    setShowSwitchConfirm(false);
    setSwitchConfirmed(false);
    setSummaryTilesDfyModel(null);
    setDfyModel('');
    setDfyConcurrency('');
    setDfyJobCap('');
    setTierSchedule([]);
    setShowTierEditor(false);
    setDiyForm({ diyEnabled: false, diyJobCap: '', diyUsed: 0, allowedTrades: [] });
    setCategories([]);
    setSelectedCategories([]);
    setContractPricing(getEmptyBeamContractPricing());
  };

  const handleSetDates = date => {
    if (!date || String(date).length < 10) {
      setContractInfo(prev => ({ ...prev, start_date: date || '', end_date: '' }));
      return;
    }
    setContractInfo(prev => ({ ...prev, start_date: date, end_date: addOneYearToDate(date) }));
  };

  const handleContractDataChange = (val, field) => {
    setContractInfo(prev => ({ ...prev, [field]: val }));
  };

  const handleBillingDataChange = (val, field) => {
    setContractInfo(prev => ({ ...prev, [field]: val }));
  };

  const handleCompanyInfoChange = (field, val) => {
    setFormData(prev => ({ ...prev, [field]: val }));
  };

  const addTierRow = () => setTierSchedule(prev => [...prev, { max_sheets: '', units: '' }]);
  const updateTierRow = (i, field, val) =>
    setTierSchedule(prev => {
      const r = [...prev];
      r[i] = { ...r[i], [field]: val };
      return r;
    });
  const removeTierRow = i => setTierSchedule(prev => prev.filter((_, idx) => idx !== i));

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await fetchApiData({ endpoint: GET_CATEGORIES, apiRequest: 'GET' });
      setCategories(data || []);
    } catch {
      setCategories([]);
    }
  }, [setCategories]);

  useEffect(() => {
    if (dataLoaded && dfyModel === 'usage_based') {
      fetchCategories();
    }
  }, [dataLoaded, dfyModel, fetchCategories]);

  const handleCategorySelect = e => {
    const categoryId = e.target.value;
    if (!categoryId || selectedCategories.includes(categoryId)) return;
    setSelectedCategories(prev => [...prev, categoryId]);
    setContractPricing(prev => ({
      ...prev,
      trade_wise_pricing: { ...prev.trade_wise_pricing, [categoryId]: { fresh: '', addendum: '' } }
    }));
  };

  const handleCategoryPricingChange = (categoryId, type, value) => {
    setContractPricing(prev => ({
      ...prev,
      trade_wise_pricing: {
        ...prev.trade_wise_pricing,
        [categoryId]: { ...prev.trade_wise_pricing?.[categoryId], [type]: value }
      }
    }));
  };

  const handleDeleteCategory = categoryId => {
    setSelectedCategories(prev => prev.filter(id => id !== categoryId));
    setContractPricing(prev => {
      const updated = { ...prev.trade_wise_pricing };
      delete updated[categoryId];
      return { ...prev, trade_wise_pricing: updated };
    });
  };

  const handleTradeToggle = trade => {
    setDiyForm(prev => ({
      ...prev,
      allowedTrades: prev.allowedTrades.some(t => t.id === trade.id)
        ? prev.allowedTrades.filter(t => t.id !== trade.id)
        : [...prev.allowedTrades, trade]
    }));
  };

  return (
    <div className='flex flex-col gap-3 my-4'>
      {/* Fetch / Clear trigger */}
      <div className='flex gap-2 items-center'>
        <Button
          color='primary'
          variant='contained'
          loading={loading}
          onClick={dataLoaded ? handleClear : fetchEnterpriseInfo}>
          {dataLoaded ? 'Clear' : 'Next'}
        </Button>
      </div>

      {dataLoaded && (
        <div className='overflow-hidden'>
          <div className='flex flex-col'>
            {isFsSynced ? (
              <InfoBanner variant='purple' icon='⤵'>
                <strong>Synced from csm.app.attentive.ai.</strong> Company info, contract dates, seats, and billing info
                are read-only here. To edit them, go to csm.app.attentive.ai → Update Enterprise Contract.
              </InfoBanner>
            ) : (
              <InfoBanner variant='info' icon='ℹ'>
                <strong>Editable company profile.</strong>{' '}
                {updateEnterprise
                  ? 'Not synced with csm.app.attentive.ai — you can edit company info, contract terms, and billing here.'
                  : 'This company is not FS-synced. Enter company info, contract terms, and billing below; commercial setup and trades load from the pre-conversion snapshot.'}
              </InfoBanner>
            )}

            <CompanyInfoSection
              data={companyFormSlice}
              readOnly={topSectionReadOnly}
              onFieldChange={handleCompanyInfoChange}
            />
            <ContractTermsSection
              contractInfo={contractInfo}
              setContractInfo={setContractInfo}
              handleDataChange={handleContractDataChange}
              setDates={handleSetDates}
              readOnly={topSectionReadOnly}
              totalUsers={formData?.total_users ?? ''}
              onTotalUsersChange={val => setFormData(prev => ({ ...prev, total_users: val }))}
            />
            <BillingInfoSection
              companyName={contractInfo?.company_name ?? ''}
              billingEmail={contractInfo?.billing_email ?? ''}
              address={contractInfo?.address ?? ''}
              city={contractInfo?.city ?? ''}
              zipcode={contractInfo?.zip ?? ''}
              country={contractInfo?.country ?? ''}
              handleDataChange={handleBillingDataChange}
              readOnly={topSectionReadOnly}
            />

            <hr className='border-gray-200 my-2' />

            {/* DFY scope / SOP gate */}
            <SectionCard title='DFY scope / SOP'>
              {sopConfigured ? (
                <InfoBanner variant='new' icon='✓'>
                  SOP is configured. Conversion can proceed.
                </InfoBanner>
              ) : (
                <InfoBanner variant='warn' icon='!'>
                  <strong>SOP must be configured before this conversion can save.</strong> Open csm.app.ibeam.ai → SOP
                  Management, configure trades and scope items, then return to this form.
                </InfoBanner>
              )}
            </SectionCard>

            <hr className='border-gray-200 my-2' />

            {/* Multi-Trade Commercial Setup */}
            <SectionCard title='Multi-Trade Commercial Setup'>
              {summaryTilesDfyModel == null ? (
                <>
                  <InfoBanner variant='new' icon='+'>
                    This section determines the DFY model. The selection drives which pricing/cap fields are required.
                    DIY is configured independently below.
                  </InfoBanner>
                  <FieldWrapper
                    label='DFY contract model'
                    required
                    help='Usage-based is priced per chargeable sheet (existing behavior). License-based uses a job-cap with hard-blocking when exhausted.'>
                    <Select
                      size='large'
                      color='secondary'
                      items={DFY_MODEL_SELECT_ITEMS}
                      value={dfyModel}
                      onChange={handleDfyModelChange}
                      placeholder='Select DFY model...'
                      variant='std-filled'
                    />
                  </FieldWrapper>
                </>
              ) : (
                <>
                  <FieldWrapper
                    label='DFY contract model'
                    required
                    help='To change the model, use the "Switch DFY model" action below. Direct editing is disabled to prevent accidental commercial changes.'>
                    <select
                      disabled
                      className='form-select form-select-sm'
                      style={{ background: '#f6f8fa', maxWidth: '340px' }}>
                      <option>
                        {dfyModel === 'license_based' ? 'DFY license-based (current)' : 'DFY usage-based (current)'}
                      </option>
                    </select>
                  </FieldWrapper>
                  <InfoBanner variant='warn' icon='!'>
                    <strong>Switching is one-way (v1).</strong> Usage-based → license-based is supported. License-based
                    → usage-based is not supported in v1.
                  </InfoBanner>
                  {dfyModel === 'usage_based' ? (
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button
                        type='button'
                        className='btn btn-outline-secondary btn-sm'
                        onClick={() => setShowSwitchConfirm(true)}>
                        Switch DFY model →
                      </button>
                      <span style={{ fontSize: '11.5px', color: '#718096' }}>
                        Reveals confirmation before applying.
                      </span>
                    </div>
                  ) : (
                    <Typography
                      variant='body4'
                      color='var(--color-neutral-50)'
                      sx={{ fontSize: '11.5px', marginTop: '4px' }}>
                      Already on license-based. Switching back to usage-based is not supported in v1.
                    </Typography>
                  )}
                </>
              )}
            </SectionCard>

            <MultiTradeSection
              dfyModel={dfyModel}
              showSwitchConfirm={updateEnterprise ? showSwitchConfirm : false}
              setShowSwitchConfirm={setShowSwitchConfirm}
              handleSwitchConfirm={handleSwitchConfirm}
              dfyJobCap={dfyJobCap}
              setDfyJobCap={setDfyJobCap}
              tierSchedule={tierSchedule}
              setTierSchedule={setTierSchedule}
              showTierEditor={showTierEditor}
              setShowTierEditor={setShowTierEditor}
              addTierRow={addTierRow}
              updateTierRow={updateTierRow}
              removeTierRow={removeTierRow}
              contractPricing={contractPricing}
              setContractPricing={setContractPricing}
              categories={categories}
              selectedCategories={selectedCategories}
              handleCategorySelect={handleCategorySelect}
              handleCategoryPricingChange={handleCategoryPricingChange}
              handleDeleteCategory={handleDeleteCategory}
            />

            <hr className='border-gray-200 my-2' />

            {/* Construction concurrency */}
            <SectionCard title='Construction concurrency'>
              <FieldWrapper
                label='DFY Concurrency Limit'
                required
                help='Maximum DFY construction jobs in processing/in-progress/review at once. Queued jobs do not count. Can be 0 (blocks DFY processing entirely).'>
                <Input
                  type='number'
                  size='large'
                  value={dfyConcurrency}
                  onChange={e => {
                    const v = e.target.value;
                    setDfyConcurrency(v);
                    setFormData(prev => ({ ...prev, concurrency_value: v === '' ? '' : Number(v) }));
                  }}
                  placeholder='0'
                />
              </FieldWrapper>
            </SectionCard>

            <hr className='border-gray-200 my-2' />

            <DIYEntitlementSection
              form={diyForm}
              setForm={setDiyForm}
              diyRemaining={diyRemaining}
              handleTradeToggle={handleTradeToggle}
              email={email}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BeamEntScreen;
